import { CloseSmall } from '@shared/assets/icons/components';
import { Flex, Modal, Typography, type ModalProps, Button } from 'antd';
import { type FC, type ReactNode } from 'react';
import styles from './confirm-modal.module.scss';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

interface ConfirmModalProps extends Omit<ModalProps, 'children' | 'onClose' | 'isOpen'> {
  isOpen: boolean;
  title: string;
  text: string | ReactNode;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  confirmButtonDanger?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  onCancelButton: () => void;
  isLoading?: boolean;
}

export const ConfirmModal: FC<ConfirmModalProps> = ({
  isOpen,
  title,
  text,
  confirmButtonLabel,
  cancelButtonLabel,
  confirmButtonDanger = false,
  onConfirm,
  onClose,
  onCancelButton,
  isLoading = false,
  ...modalProps
}) => {
  const { Text } = Typography;
  const { t } = useTranslation('translation');
  const resolvedConfirmLabel = confirmButtonLabel ?? t('common.actions.confirm');
  const resolvedCancelLabel = cancelButtonLabel ?? t('common.actions.cancel');

  return (
    <Modal
      centered
      cancelButtonProps={{ hidden: true }}
      okButtonProps={{ hidden: true }}
      closeIcon={<CloseSmall color="#A7A8AE" />}
      width="500px"
      {...modalProps}
      open={isOpen}
      onCancel={onClose}
    >
      <Flex gap="24px" vertical justify="center" align="center">
        <Flex vertical gap="16px">
          <Text className={styles.title}>{title}</Text>
          <Text className={styles.text}>{text}</Text>
        </Flex>

        <Flex gap="16px" align="center" justify="center" className={styles.button_container}>
          <Button
            variant="outlined"
            disabled={isLoading}
            onClick={onCancelButton}
            size="large"
            className={classNames(styles.button, styles.button_cancel)}
          >
            {resolvedCancelLabel}
          </Button>

          <Button
            type="primary"
            danger={confirmButtonDanger}
            htmlType="submit"
            loading={isLoading}
            onClick={onConfirm}
            size="large"
            className={styles.button}
          >
            {resolvedConfirmLabel}
          </Button>
        </Flex>
      </Flex>
    </Modal>
  );
};
